import React from 'react';

/**
 * Card Component
 * A reusable container with shadow, padding, and dark mode support
 */
const Card = ({
  children,
  className = '',
  padding = 'md',
  shadow = 'md',
  hover = false,
  onClick,
  as: Component = 'div',
  ...props
}) => {
  const paddings = {
    none: '',
    sm: 'p-3',
    md: 'p-4 md:p-6',
    lg: 'p-6 md:p-8',
  };

  const shadows = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  };

  const baseStyles = `
    bg-white dark:bg-gray-800
    rounded-lg
    ${paddings[padding]}
    ${shadows[shadow]}
    ${hover ? 'hover:shadow-lg transition-shadow duration-200 cursor-pointer' : ''}
    ${onClick ? 'cursor-pointer' : ''}
  `;

  return (
    <Component
      className={`${baseStyles} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * Card Header Component
 */
export const CardHeader = ({ children, className = '', ...props }) => (
  <div
    className={`border-b border-gray-200 dark:border-gray-700 pb-4 mb-4 ${className}`}
    {...props}
  >
    {children}
  </div>
);

/**
 * Card Title Component
 */
export const CardTitle = ({ children, className = '', as: Component = 'h3', ...props }) => (
  <Component
    className={`text-lg font-semibold text-gray-900 dark:text-gray-100 ${className}`}
    {...props}
  >
    {children}
  </Component>
);

/**
 * Card Description Component
 */
export const CardDescription = ({ children, className = '', ...props }) => (
  <p
    className={`text-sm text-gray-500 dark:text-gray-400 mt-1 ${className}`}
    {...props}
  >
    {children}
  </p>
);

/**
 * Card Content Component
 */
export const CardContent = ({ children, className = '', ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

/**
 * Card Footer Component
 */
export const CardFooter = ({ children, className = '', ...props }) => (
  <div
    className={`border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 ${className}`}
    {...props}
  >
    {children}
  </div>
);

export default Card;

